import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Calendar, Phone, Mail, User } from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import TurnstileWidget from './TurnstileWidget';
import useReducedMotion from '../hooks/useReducedMotion';
import { lockPageScroll } from '../utils/smoothScroll';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionSecret, setSessionSecret] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [bookingTurnstileToken, setBookingTurnstileToken] = useState('');
  const [displayedResponse, setDisplayedResponse] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestedSlots, setSuggestedSlots] = useState([]);
  const [bookingStep, setBookingStep] = useState(null); // null, 'select_slot', 'collect_name', 'collect_phone', 'collect_email', 'collect_reason', 'confirming'
  const [bookingData, setBookingData] = useState({
    slot: null,
    name: '',
    phone: '',
    email: '',
    reason: ''
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const bookingIdempotencyKeyRef = useRef(crypto.randomUUID());
  const typingTimerRef = useRef(null);
  const typingRunRef = useRef(0);
  const typingResultRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(typingTimerRef.current);
    typingRunRef.current += 1;
  }, []);

  useEffect(() => {
    // Finish, rather than animate invisibly, when closing or reducing motion.
    const pending = typingResultRef.current;
    if ((!isOpen || reducedMotion) && pending) {
      clearTimeout(typingTimerRef.current);
      typingRunRef.current += 1;
      typingResultRef.current = null;
      setIsTyping(false);
      setDisplayedResponse('');
      setMessages(prev => pending.isWelcome
        ? [{ role: 'assistant', content: pending.text }]
        : [...prev, { role: 'assistant', content: pending.text }]);
    }
  }, [isOpen, reducedMotion, isTyping]);

  useEffect(() => {
    if (!isOpen) return;
    const fullscreen = window.matchMedia('(max-width: 639px)');
    let release = null;
    const syncLock = () => {
      if (fullscreen.matches && !release) release = lockPageScroll();
      else if (!fullscreen.matches && release) { release(); release = null; }
    };
    syncLock();
    fullscreen.addEventListener('change', syncLock);
    return () => {
      fullscreen.removeEventListener('change', syncLock);
      release?.();
    };
  }, [isOpen]);

  // Messaggio di benvenuto
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMsg = "Ciao! Sono Matteo, l’assistente AI di Nicola. Dimmi, cosa ti porta qui oggi?";
      simulateTyping(welcomeMsg, true);
    }
  }, [isOpen]);

  // Auto-scroll ai nuovi messaggi
  useEffect(() => {
    const scroller = messagesEndRef.current?.parentElement;
    // Scroll only the message list, never the document or a hidden chat window.
    if (isOpen && scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [isOpen, messages, displayedResponse, bookingStep]);

  // Focus sull'input quando si apre la chat
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Funzione per simulare la digitazione umana
  const simulateTyping = (text, isWelcome = false) => {
    clearTimeout(typingTimerRef.current);
    const run = ++typingRunRef.current;
    typingResultRef.current = { text, isWelcome };
    if (reducedMotion) {
      typingResultRef.current = null;
      setIsTyping(false);
      setDisplayedResponse('');
      setMessages(prev => isWelcome ? [{ role: 'assistant', content: text }] : [...prev, { role: 'assistant', content: text }]);
      return;
    }
    setIsTyping(true);
    setDisplayedResponse('');
    
    let index = 0;
    const baseDelay = 25;
    
    const typeChar = () => {
      if (run !== typingRunRef.current) return;
      if (index < text.length) {
        const char = text[index];
        setDisplayedResponse(prev => prev + char);
        index++;
        
        let delay = baseDelay;
        if (['.', '!', '?'].includes(char)) {
          delay = 300 + Math.random() * 150;
        } else if ([',', ';', ':'].includes(char)) {
          delay = 100 + Math.random() * 80;
        } else if (char === ' ') {
          delay = Math.random() > 0.9 ? 150 + Math.random() * 100 : baseDelay;
        } else {
          delay = baseDelay + Math.random() * 15;
        }
        
        typingTimerRef.current = setTimeout(typeChar, delay);
      } else {
        typingResultRef.current = null;
        setIsTyping(false);
        if (isWelcome) {
          setMessages([{ role: 'assistant', content: text }]);
        } else {
          setMessages(prev => [...prev, { role: 'assistant', content: text }]);
        }
        setDisplayedResponse('');
      }
    };
    
    typingTimerRef.current = setTimeout(typeChar, 400 + Math.random() * 400);
  };

  // Helper function to extract date from user message
  const extractDateFromMessage = (message) => {
    const msg = message.toLowerCase();
    const today = new Date();
    const giorni = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
    
    // Check for day names
    for (let i = 0; i < 7; i++) {
      const targetDay = giorni[i];
      if (msg.includes(targetDay)) {
        // Find the next occurrence of this day
        const currentDay = today.getDay();
        let daysToAdd = i - currentDay;
        if (daysToAdd <= 0) daysToAdd += 7;
        
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + daysToAdd);
        
        // Extract time if mentioned
        const timeMatch = msg.match(/(\d{1,2})[:\.]?(\d{2})?\s*(di mattina|di pomeriggio|mattina|pomeriggio)?/);
        let hour = 10; // default
        if (timeMatch) {
          hour = parseInt(timeMatch[1]);
          if (timeMatch[3] && timeMatch[3].includes('pomeriggio') && hour < 12) {
            hour += 12;
          }
        } else if (msg.includes('mattina')) {
          hour = 10;
        } else if (msg.includes('pomeriggio')) {
          hour = 15;
        }
        
        const minute = timeMatch && timeMatch[2] ? parseInt(timeMatch[2]) : 0;
        
        return {
          date: targetDate.toISOString().split('T')[0],
          time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        };
      }
    }
    
    return null;
  };

  // Check slot availability
  const checkSlotAvailability = async (date, time) => {
    try {
      const response = await apiFetch(`${API_URL}/api/calendar/check-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, time })
      });
      return await response.json();
    } catch (error) {
      console.error('Error checking slot:', error);
      return null;
    }
  };

  const sendMessage = async (messageOverride = null) => {
    const messageToSend = messageOverride || inputValue.trim();
    if (!messageToSend || isLoading || isTyping || (!sessionId && !turnstileToken)) return;

    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
    setIsLoading(true);

    // Check if user is proposing a specific date
    let additionalContext = '';
    const extractedDate = extractDateFromMessage(messageToSend);
    
    if (extractedDate && suggestedSlots.length > 0) {
      const availability = await checkSlotAvailability(extractedDate.date, extractedDate.time);
      
      if (availability) {
        if (availability.available) {
          additionalContext = `\n\n[SISTEMA: L'utente ha chiesto ${extractedDate.date} alle ${extractedDate.time}. HO VERIFICATO: LO SLOT È DISPONIBILE! Procedi a confermare questo orario e raccogli i dati per la prenotazione.]`;
          // Add this as a new available slot
          setSuggestedSlots(prev => [{
            date: availability.slot.date,
            time: availability.slot.time,
            display: availability.slot.display
          }, ...prev.slice(0, 1)]);
        } else if (availability.alternative) {
          additionalContext = `\n\n[SISTEMA: L'utente ha chiesto ${extractedDate.date} alle ${extractedDate.time}. HO VERIFICATO: OCCUPATO. Proponi l'alternativa più vicina: ${availability.alternative.display}]`;
        } else {
          additionalContext = `\n\n[SISTEMA: L'utente ha chiesto ${extractedDate.date} alle ${extractedDate.time}. HO VERIFICATO: OCCUPATO e nessuna alternativa disponibile in quel giorno. Proponi gli slot originali.]`;
        }
      }
    }

    try {
      const response = await apiFetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          session_secret: sessionSecret,
          turnstileToken: sessionId ? undefined : turnstileToken,
          message: messageToSend + additionalContext,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Richiesta chat non riuscita');
      
      if (!sessionId) {
        setSessionId(data.session_id);
        setSessionSecret(data.session_secret);
      }

      let assistantReply = data.response;
      // Store suggested slots if available
      if (data.suggested_slots && data.suggested_slots.length > 0) {
        setSuggestedSlots(data.suggested_slots);
      } else if (/call|appuntamento|prenot|disponibil/i.test(messageToSend + ' ' + data.response)) {
        const slotsResponse = await apiFetch(`${API_URL}/api/calendar/available-slots`);
        const availability = await slotsResponse.json();
        if (slotsResponse.ok && availability.connected) {
          const verifiedSlots = availability.slots || [];
          setSuggestedSlots(verifiedSlots);
          // The model has no Calendar access: availability must come from the
          // provider response, not from a claim generated by the assistant.
          if (verifiedSlots.length && /prenot|appuntamento|slot|disponibil/i.test(messageToSend)) {
            assistantReply = 'Ecco le disponibilità aggiornate dal calendario di Nicola. Scegli uno slot qui sotto per continuare con la prenotazione.';
          }
        } else {
          setSuggestedSlots([]);
        }
      }

      setIsLoading(false);
      simulateTyping(assistantReply);
      
    } catch (error) {
      console.error('Errore invio messaggio:', error);
      setIsLoading(false);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi scuso, c\'è stato un problema di connessione. Puoi riprovare?' 
      }]);
    }
  };

  const handleSlotSelection = (slot) => {
    setBookingData(prev => ({ ...prev, slot }));
    setBookingStep('collect_name');
    setMessages(prev => [...prev, 
      { role: 'user', content: `Preferisco ${slot.display}` },
      { role: 'assistant', content: 'Perfetto! Mi dai il tuo nome e cognome?' }
    ]);
  };

  const handleBookingInput = async () => {
    const value = inputValue.trim();
    if (!value) return;

    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: value }]);

    if (bookingStep === 'collect_name') {
      setBookingData(prev => ({ ...prev, name: value }));
      setBookingStep('collect_phone');
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Ottimo! E un numero di telefono dove Nicola possa contattarti?' 
        }]);
      }, 500);
    } else if (bookingStep === 'collect_phone') {
      setBookingData(prev => ({ ...prev, phone: value }));
      setBookingStep('collect_email');
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Perfetto, e la tua email per inviarti la conferma?' 
        }]);
      }, 500);
    } else if (bookingStep === 'collect_email') {
      setBookingData(prev => ({ ...prev, email: value }));
      setBookingStep('collect_reason');
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Ultimo step: in due parole, qual è la sfida principale di cui vorresti parlare con Nicola?' 
        }]);
      }, 500);
    } else if (bookingStep === 'collect_reason') {
      const finalData = { ...bookingData, reason: value };
      setBookingData(finalData);
      setBookingStep('confirm');
      
      // Show confirmation message
      const firstName = finalData.name.split(' ')[0];
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Perfetto ${firstName}! Riepiloghiamo:\n\n📅 ${finalData.slot.display}\n👤 ${finalData.name}\n📞 ${finalData.phone}\n✉️ ${finalData.email}\n\nConfermi la prenotazione?`
        }]);
      }, 500);
    } else if (bookingStep === 'confirm') {
      const lowerValue = value.toLowerCase();
      if (lowerValue.includes('sì') || lowerValue.includes('si') || lowerValue.includes('conferm') || lowerValue.includes('ok') || lowerValue.includes('va bene') || lowerValue.includes('perfetto')) {
        if (!bookingTurnstileToken) {
          setMessages(prev => [...prev, { role: 'assistant', content: 'Completa prima la verifica antispam qui sotto, poi conferma nuovamente.' }]);
          return;
        }
        setBookingStep('confirming');
        await confirmBooking(bookingData);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Nessun problema! Se vuoi modificare qualcosa o prenotare in un altro momento, sono qui. Come posso aiutarti?'
        }]);
        setBookingStep(null);
        setBookingData({ slot: null, name: '', phone: '', email: '', reason: '' });
      }
    }
  };

  const confirmBooking = async (data) => {
    setIsLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/calendar/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': bookingIdempotencyKeyRef.current,
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          date: data.slot.date,
          time: data.slot.time,
          notes: data.reason,
          turnstileToken: bookingTurnstileToken,
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Perfetto ${data.name.split(' ')[0]}! Appuntamento confermato per ${data.slot.display}. Riceverai una email di conferma a ${data.email}. Nicola ti aspetta, a presto!`
        }]);
        setBookingStep(null);
        setBookingData({ slot: null, name: '', phone: '', email: '', reason: '' });
        bookingIdempotencyKeyRef.current = crypto.randomUUID();
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Mi dispiace, c\'è stato un problema con la prenotazione. Puoi riprovare o contattarci direttamente via email a info@nicolascerni.it'
        }]);
        setBookingStep('confirm');
      }
    } catch (error) {
      console.error('Booking error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi dispiace, c\'è stato un errore tecnico. Puoi contattarci direttamente via email a info@nicolascerni.it'
      }]);
      setBookingStep('confirm');
    } finally {
      setIsLoading(false);
      setBookingTurnstileToken('');
    }
  };

  const showSlotButtons = () => {
    // Mostra i pulsanti slot quando il chatbot menziona la disponibilità
    const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAssistantMsg) return false;
    
    const text = lastAssistantMsg.content.toLowerCase();
    const mentionsAvailability = text.includes('disponibilità') || 
                                 text.includes('possibilità') ||
                                 text.includes('slot') ||
                                 text.includes('quale preferisci') ||
                                 text.includes('quale ti è più comodo');
    
    return suggestedSlots.length > 0 && mentionsAvailability && !bookingStep && !isTyping;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (bookingStep) {
        handleBookingInput();
      } else {
        sendMessage();
      }
    }
  };

  const getPlaceholder = () => {
    if (bookingStep === 'collect_name') return 'Es: Mario Rossi';
    if (bookingStep === 'collect_phone') return 'Es: 333 1234567';
    if (bookingStep === 'collect_email') return 'Es: mario@azienda.it';
    if (bookingStep === 'collect_reason') return 'Es: Voglio aumentare le vendite...';
    if (bookingStep === 'confirm') return 'Scrivi "sì" per confermare...';
    return 'Scrivi un messaggio...';
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 sm:bottom-8 right-4 sm:right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 bg-[#d4883a] hover:bg-[#d4896a] text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
        data-testid="chatbot-open-btn"
      >
        <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7" />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-0 sm:bottom-8 right-0 sm:right-6 z-50 w-full sm:w-[400px] sm:max-w-[calc(100vw-48px)] h-[100dvh] sm:h-[600px] sm:max-h-[calc(100vh-100px)] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 origin-bottom-right ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}
        data-testid="chatbot-window"
        data-lenis-prevent
      >
        {/* Header */}
        <div className="bg-[#0b114e] px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between flex-shrink-0 safe-area-inset-top">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden flex-shrink-0">
              <img 
                src="/media/matteo.webp"
                alt="Matteo"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Matteo</h3>
              <p className="text-white/60 text-xs">Assistente AI di Nicola</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/70 hover:text-white transition-colors p-2 -mr-2"
            data-testid="chatbot-close-btn"
          >
            <X className="w-6 h-6 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4 bg-gray-50" data-lenis-prevent>
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-[#0b114e] text-white rounded-br-md'
                    : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {/* Slot Selection Buttons */}
          {showSlotButtons() && (
            <div className="flex flex-col gap-2 px-2">
              <p className="text-xs text-gray-500 text-center mb-1">Scegli uno slot:</p>
              {suggestedSlots.slice(0, 2).map((slot, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSlotSelection(slot)}
                  className="w-full py-3 px-4 bg-[#0b114e] hover:bg-[#0b114e]/90 text-white rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                  data-testid={`slot-btn-${idx}`}
                >
                  <Calendar className="w-4 h-4" />
                  {slot.display}
                </button>
              ))}
            </div>
          )}
          
          {/* Typing indicator / Response being typed */}
          {(isLoading || isTyping) && (
            <div className="flex justify-start">
              <div className="max-w-[85%] px-4 py-3 rounded-2xl bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md">
                {isLoading ? (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {displayedResponse}
                    <span className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 animate-pulse" />
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Booking Step Indicator */}
          {bookingStep && bookingStep !== 'confirming' && (
            <div className="flex items-center justify-center gap-2 py-2">
              <div className="flex gap-1">
                {['collect_name', 'collect_phone', 'collect_email', 'collect_reason', 'confirm'].map((step, idx) => (
                  <div 
                    key={step}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      ['collect_name', 'collect_phone', 'collect_email', 'collect_reason', 'confirm'].indexOf(bookingStep) >= idx
                        ? 'bg-[#d4883a]'
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500">
                {bookingStep === 'collect_name' && 'Nome'}
                {bookingStep === 'collect_phone' && 'Telefono'}
                {bookingStep === 'collect_email' && 'Email'}
                {bookingStep === 'collect_reason' && 'Motivo'}
                {bookingStep === 'confirm' && 'Conferma'}
              </span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-3 sm:p-4 bg-white border-t border-gray-100 flex-shrink-0 safe-area-inset-bottom">
          <p className="text-xs leading-relaxed text-gray-600 mb-3" id="chatbot-privacy-notice">
            Parli con un assistente AI: i messaggi vengono inviati a OpenAI e le risposte possono essere inesatte. Non inserire dati sensibili o riservati.{' '}
            <a href="/privacy-policy" className="text-[#9a5a1c] underline">Privacy</a>. Puoi anche{' '}
            <a href="mailto:nicola@nicolascerni.com" className="text-[#9a5a1c] underline">scrivere a Nicola</a>.
          </p>
          {!sessionId && <TurnstileWidget action="chat" onVerify={setTurnstileToken} />}
          {bookingStep === 'confirm' && <TurnstileWidget action="booking" onVerify={setBookingTurnstileToken} />}
          <div className="flex items-center gap-2">
            {/* Icon indicator for booking step */}
            {bookingStep && (
              <div className="w-10 h-10 flex items-center justify-center text-[#d4883a]">
                {bookingStep === 'collect_name' && <User className="w-5 h-5" />}
                {bookingStep === 'collect_phone' && <Phone className="w-5 h-5" />}
                {bookingStep === 'collect_email' && <Mail className="w-5 h-5" />}
                {bookingStep === 'collect_reason' && <MessageCircle className="w-5 h-5" />}
                {bookingStep === 'confirm' && <Calendar className="w-5 h-5" />}
              </div>
            )}
            <input
              ref={inputRef}
              type={bookingStep === 'collect_email' ? 'email' : 'text'}
              inputMode={bookingStep === 'collect_phone' ? 'tel' : 'text'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={getPlaceholder()}
              disabled={isLoading || isTyping || bookingStep === 'confirming'}
              className="flex-1 px-4 py-2.5 sm:py-3 bg-gray-100 rounded-full text-base focus:outline-none focus:ring-2 focus:ring-[#d4883a]/50 disabled:opacity-50"
              style={{ fontSize: '16px' }}
              data-testid="chatbot-input"
              aria-describedby="chatbot-privacy-notice"
            />
            <button
              onClick={bookingStep ? handleBookingInput : () => sendMessage()}
              disabled={!inputValue.trim() || isLoading || isTyping || bookingStep === 'confirming' || (!sessionId && !turnstileToken)}
              className="w-10 h-10 sm:w-11 sm:h-11 bg-[#d4883a] hover:bg-[#d4896a] disabled:bg-gray-300 text-white rounded-full flex items-center justify-center transition-colors disabled:cursor-not-allowed flex-shrink-0"
              data-testid="chatbot-send-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chatbot;
